<template>
    <form autocomplete="off" :class="'max-w-lg mx-auto ml-2 ' + props.class" @submit.prevent="doSearch()">
    <label for="default-search" class="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-gray-300">{{ $t("home.hero.search_button") }}</label>
    <div class="relative">
        <input 
            ref="searchInput"
            v-model="searchQuery" 
            type="search" 
            id="default-search" 
            class="block w-full p-4 text-sm text-gray-700 border border-gray-300 rounded-lg bg-gray-50 focus:ring-primaryBg focus:border-primaryBg dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500" 
            :placeholder="$t('home.hero.search_placeholder')" 
            required
            @input="onInputChange"
            @keydown="onKeyDown"
            @blur="onInputBlur"
            @focus="onInputFocus"
        >
        <button type="submit" class="text-white inline-flex items-center absolute right-2.5 bottom-2.5 bg-secondaryBtn/90 hover:bg-secondaryBtnHover font-medium rounded-lg text-sm px-4 py-2 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800">
            <svg class="w-4 h-4 mr-2 -ml-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"></path></svg>
            {{ $t("home.hero.search_button") }}  
        </button>
        
        <!-- Suggestions Dropdown -->
        <div 
            v-if="showSuggestions && (suggestions.length > 0 || isLoadingSuggestions)"
            class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto dark:bg-gray-700 dark:border-gray-600 suggestions-dropdown"
        >
            <!-- Loading state -->
            <div v-if="isLoadingSuggestions" class="px-4 py-3 text-center text-gray-500 dark:text-gray-400">
                <div class="flex items-center justify-center">
                    <svg class="animate-spin -ml-1 mr-3 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    搜索中...
                </div>
            </div>
            
            <!-- Suggestions list -->
            <div
                v-for="(suggestion, index) in suggestions"
                :key="suggestion.id"
                :class="[
                    'px-4 py-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 suggestion-item',
                    selectedSuggestionIndex === index ? 'bg-gray-100 dark:bg-gray-600' : ''
                ]"
                @click="selectSuggestion(suggestion)"
                @mouseenter="selectedSuggestionIndex = index"
            >
                <div class="flex items-center">
                    <!-- Icon based on suggestion type -->
                    <div class="flex-shrink-0 mr-3">
                        <svg v-if="suggestion.type === 'doctor'" class="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"></path>
                        </svg>
                        <svg v-else-if="suggestion.type === 'specialty'" class="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clip-rule="evenodd"></path>
                        </svg>
                    </div>
                    
                    <!-- Suggestion content -->
                    <div class="flex-1 min-w-0">
                        <div class="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {{ suggestion.name }}
                            <span v-if="suggestion.verified" class="ml-1 text-green-500" title="已验证医生">✓</span>
                        </div>
                        <div v-if="suggestion.specialty && suggestion.type === 'doctor'" class="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {{ suggestion.specialty }}
                        </div>
                    </div>

                    <!-- Suggestion type indicator -->
                    <div class="flex-shrink-0 ml-2">
                        <span 
                            :class="[
                                'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                                suggestion.type === 'doctor' ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100' : 
                                'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100'
                            ]"
                        >
                            {{ suggestion.type === 'doctor' ? '医生' : '专科' }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <!-- SQ : {{ searchQuery }} -->
    <!-- {{ result }} -->
    </form>      
    
</template>

<script setup lang="ts"> 

const props = defineProps(['class'])

// Suggestion type definition for TypeScript
type Suggestion = {
    id: string
    text: string
    name: string
    specialty?: string
    verified?: boolean
    type: 'doctor' | 'specialty'
}

const { searchQuery, searchResults, getSearchResults } = useSearch()

const route = useRouter()
const searchInput = ref<HTMLInputElement>()

// Autocomplete state
const suggestions = ref<Suggestion[]>([])
const showSuggestions = ref(false)
const selectedSuggestionIndex = ref(-1)
const isLoadingSuggestions = ref(false)
const suggestionDebounceTimer = ref<NodeJS.Timeout>()

const doSearch = () => {
    console.debug("Search Input: Searching for:", searchQuery.value)
    console.debug(route.currentRoute.value)
    console.debug("Route:", route.currentRoute.value?.path == '/doctor')
    
    // Hide suggestions when searching
    hideSuggestions()
    
    if (route.currentRoute.value?.path == '/doctor') {
        if (searchQuery.value == '') {
            getSearchResults("内科")
        } else {
            getSearchResults(searchQuery.value)
        }
    }
    else{
        route.push(`/doctor?search=${searchQuery.value}`)
    }
}

/**
 * Fetch suggestions from the API with debouncing
 */
const fetchSuggestions = async (query: string) => {
    if (!query.trim() || query.length < 2) {
        suggestions.value = []
        return
    }

    isLoadingSuggestions.value = true
    
    try {
        const response = await $fetch('/api/doctor/suggestions', {
            query: { q: query },
            timeout: 3000 // 3 second timeout for suggestions
        })
        
        suggestions.value = Array.isArray(response) ? response : []
        console.debug(`[SearchInput] Found ${suggestions.value.length} suggestions for: ${query}`)
        
    } catch (error) {
        console.error('[SearchInput] Failed to fetch suggestions:', error)
        suggestions.value = []
    } finally {
        isLoadingSuggestions.value = false
    }
}

/**
 * Handle input changes with debouncing
 */
const onInputChange = () => {
    // Clear previous debounce timer
    if (suggestionDebounceTimer.value) {
        clearTimeout(suggestionDebounceTimer.value)
    }
    
    // Reset selection when typing
    selectedSuggestionIndex.value = -1
    
    const query = searchQuery.value.trim()
    
    if (query.length < 2) {
        hideSuggestions()
        return
    }
    
    // Debounce the API call to avoid too many requests
    suggestionDebounceTimer.value = setTimeout(() => {
        fetchSuggestions(query)
        showSuggestions.value = true
    }, 300) // 300ms debounce
}

/**
 * Handle keyboard navigation in suggestions
 */
const onKeyDown = (event: KeyboardEvent) => {
    if (!showSuggestions.value || suggestions.value.length === 0) {
        return
    }
    
    switch (event.key) {
        case 'ArrowDown':
            event.preventDefault()
            selectedSuggestionIndex.value = Math.min(
                selectedSuggestionIndex.value + 1,
                suggestions.value.length - 1
            )
            break
            
        case 'ArrowUp':
            event.preventDefault()
            selectedSuggestionIndex.value = Math.max(
                selectedSuggestionIndex.value - 1,
                -1
            )
            break
            
        case 'Enter':
            if (selectedSuggestionIndex.value >= 0) {
                event.preventDefault()
                selectSuggestion(suggestions.value[selectedSuggestionIndex.value])
            }
            break
            
        case 'Escape':
            hideSuggestions()
            searchInput.value?.blur()
            break
    }
}

/**
 * Handle suggestion selection
 */
const selectSuggestion = (suggestion: Suggestion) => {
    searchQuery.value = suggestion.name
    hideSuggestions()
    
    // Immediately trigger search
    nextTick(() => {
        doSearch()
    })
}

/**
 * Show suggestions when input is focused
 */
const onInputFocus = () => {
    if (searchQuery.value.trim().length >= 2 && suggestions.value.length > 0) {
        showSuggestions.value = true
    }
}

/**
 * Hide suggestions when input loses focus (with delay for click handling)
 */
const onInputBlur = () => {
    // Delay hiding to allow click on suggestions
    setTimeout(() => {
        hideSuggestions()
    }, 200)
}

/**
 * Hide suggestions dropdown
 */
const hideSuggestions = () => {
    showSuggestions.value = false
    selectedSuggestionIndex.value = -1
}

// Clean up timers when component is unmounted
onUnmounted(() => {
    if (suggestionDebounceTimer.value) {
        clearTimeout(suggestionDebounceTimer.value)
    }
})

// onBeforeMount(async () => {
// //   await search({ query: 'yulin' })
// //   console.log('result', result.value.hits[0]['Address 1'])
//   console.log("on before mount search input")
// })

</script>

<style scoped>
/* Custom scrollbar for suggestions dropdown */
.suggestions-dropdown::-webkit-scrollbar {
    width: 6px;
}

.suggestions-dropdown::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.suggestions-dropdown::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.suggestions-dropdown::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

/* Smooth transitions for suggestions */
.suggestion-item {
    transition: background-color 0.15s ease-in-out;
}

/* Loading state styling */
.loading-suggestions {
    opacity: 0.7;
}
</style>